import React, { useState, useEffect, useRef } from "react";
import { Link as RouterLink } from "react-router-dom";
import {
  Container, VStack, Heading, Text, Box, Badge, HStack,
  OrderedList, ListItem, UnorderedList, Divider, Button, Link, SimpleGrid, Icon,
  Spinner, Center, Table, Thead, Tbody, Tr, Th, Td,
  useColorMode,
} from "@chakra-ui/react";
import { FiArrowLeft, FiShield, FiClock, FiUsers, FiLock, FiDollarSign } from "react-icons/fi";
import { exportToSvg } from "@excalidraw/excalidraw";
import { NavbarWithCallToAction } from "../../components/chakra/NavbarWithCallToAction/NavbarWithCallToAction";
import { FooterWithFourColumns } from "../../components/chakra/FooterWithFourColumns/FooterWithFourColumns";

const SafeCryptoCustody: React.FC = () => {
  // Force light mode on this public page
  const { colorMode, setColorMode } = useColorMode();
  useEffect(() => {
    if (colorMode === "dark") setColorMode("light");
  }, [colorMode, setColorMode]);
  const [diagramLoading, setDiagramLoading] = useState(true);
  const [diagramError, setDiagramError] = useState<string | null>(null);
  const svgContainerRef = useRef<HTMLDivElement>(null);
  const [zkDiagramLoading, setZkDiagramLoading] = useState(true);
  const [zkDiagramError, setZkDiagramError] = useState<string | null>(null);
  const zkSvgContainerRef = useRef<HTMLDivElement>(null);

  const loadDiagram = (path: string, containerRef: React.RefObject<HTMLDivElement>, setLoading: (v: boolean) => void, setError: (v: string | null) => void) => {
    fetch(path)
      .then((res) => {
        if (!res.ok) throw new Error(`Failed to load diagram: ${res.status}`);
        return res.json();
      })
      .then(async (data) => {
        const svg = await exportToSvg({
          elements: data.elements,
          appState: { ...data.appState, exportWithDarkMode: false },
          files: data.files || {},
        });
        svg.removeAttribute("width");
        svg.removeAttribute("height");
        svg.style.width = "100%";
        svg.style.height = "auto";
        if (containerRef.current) {
          containerRef.current.innerHTML = "";
          containerRef.current.appendChild(svg);
        }
        setLoading(false);
      })
      .catch((err) => {
        setError(err.message);
        setLoading(false);
      });
  };

  useEffect(() => {
    loadDiagram("/excalidraw/safe-crypto-revenue.excalidraw", svgContainerRef, setDiagramLoading, setDiagramError);
    loadDiagram("/excalidraw/safe-crypto-zk-proofs.excalidraw", zkSvgContainerRef, setZkDiagramLoading, setZkDiagramError);
  }, []);
  return (
    <>
      <NavbarWithCallToAction />
      <Container maxW="container.md" py={10}>
        <VStack spacing={6} align="stretch">
          {/* Header */}
          <VStack spacing={3} align="start">
            <HStack>
              <Badge colorScheme="orange">Safe Crypto</Badge>
              <Text fontSize="xs" color="gray.400">22 Mar 2026</Text>
            </HStack>
            <Heading size="lg" color="#2c5282">Safe Crypto Custody — Protecting Against Social Engineering</Heading>
            <Text color="gray.600">
              Your crypto is one phone call away from being stolen. We're building a solution that makes social engineering attacks useless — using time delays and human verification.
            </Text>
          </VStack>

          <Divider />

          {/* The Problem */}
          <Box>
            <Heading size="md" color="#c53030" mb={3}>The Problem</Heading>
            <Text color="gray.600" mb={4}>
              <strong>Ed's story:</strong> A hacker called Ed claiming his wallet had been compromised and needed to be "secured" urgently. In the panic, Ed entered his hardware wallet seed words into a website the hacker directed him to. Instantly, all his crypto was gone. The attacker didn't break any encryption — they just tricked a human under pressure.
            </Text>
            <Text color="gray.600" mb={4}>
              <strong>Tom's story:</strong> Tom's Google account was hacked via social engineering — his Google password had been leaked online. This proves that centralised monolithic systems like Google aren't safe for storing passkeys, passwords, or anything sensitive. If one password leaks, everything connected to it is exposed.
            </Text>
            <Text color="gray.600" mb={4}>
              These aren't isolated incidents — social engineering is the #1 way people lose crypto. The attacks exploit urgency and trust, not technical vulnerabilities. Saving seed phrases on laptops or in cloud services means one breach = total loss.
            </Text>
            <Box bg="red.50" p={4} borderRadius="md" border="1px solid" borderColor="red.200">
              <Text fontWeight="bold" color="red.700" mb={1}>The core issue:</Text>
              <Text color="red.600">No delay. No verification. No recovery. One mistake = everything gone instantly.</Text>
            </Box>
          </Box>

          <Divider />

          {/* The Solution */}
          <Box>
            <Heading size="md" color="#2b6cb0" mb={3}>The Solution</Heading>
            <Text color="gray.600" mb={4}>
              Three principles that make social engineering useless:
            </Text>

            <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4}>
              <Box p={5} borderRadius="md" border="2px solid" borderColor="blue.300" bg="blue.50">
                <HStack mb={2}>
                  <Icon as={FiClock} color="blue.600" boxSize={5} />
                  <Text fontWeight="bold" fontSize="lg" color="blue.700">10-Day Time Lock</Text>
                </HStack>
                <Text fontSize="sm" color="gray.600">
                  Every withdrawal has a mandatory 10-day delay. Social engineering relies on urgency — remove urgency, remove the attack. Even if someone tricks you, you have 10 days to notice and cancel.
                </Text>
              </Box>
              <Box p={5} borderRadius="md" border="2px solid" borderColor="purple.300" bg="purple.50">
                <HStack mb={2}>
                  <Icon as={FiUsers} color="purple.600" boxSize={5} />
                  <Text fontWeight="bold" fontSize="lg" color="purple.700">Validator Network</Text>
                </HStack>
                <Text fontSize="sm" color="gray.600">
                  Need it before 10 days? A validator on the 2-of-3 multisig is contacted. They reach out to you directly using system-provided contact details, verify you're not being coerced, and approve via their own wallet. Validators don't know each other — preventing collusion.
                </Text>
              </Box>
              <Box p={5} borderRadius="md" border="2px solid" borderColor="green.300" bg="green.50">
                <HStack mb={2}>
                  <Icon as={FiShield} color="green.600" boxSize={5} />
                  <Text fontWeight="bold" fontSize="lg" color="green.700">On-Chain Signing</Text>
                </HStack>
                <Text fontSize="sm" color="gray.600">
                  Validators sign approvals with their own crypto wallet (MetaMask, hardware wallet). Even if our server is hacked, attackers can't forge approvals — they'd need to compromise each validator's individual wallet. Validators earn a fee for each verification.
                </Text>
              </Box>
            </SimpleGrid>

            <Box mt={4} bg="green.50" p={4} borderRadius="md" border="1px solid" borderColor="green.200">
              <Text fontWeight="bold" color="green.700">
                You always get your crypto back. Just not instantly.
              </Text>
            </Box>
          </Box>

          <Divider />

          {/* How It Works */}
          <Box>
            <Heading size="md" color="#2b6cb0" mb={3}>How It Works</Heading>
            <OrderedList spacing={3} pl={4} color="gray.600">
              <ListItem>
                <Text as="span" fontWeight="bold">Connect your Web3 wallet</Text> — Link your existing wallet (MetaMask, hardware wallet, etc.) to the platform
              </ListItem>
              <ListItem>
                <Text as="span" fontWeight="bold">Deposit crypto</Text> — Transfer BTC, ETH, or tokenised gold into a time-locked smart contract vault (2% deposit fee)
              </ListItem>
              <ListItem>
                <Text as="span" fontWeight="bold">Request a withdrawal</Text> — When you want your crypto back, submit a request. A 10-day countdown begins automatically
              </ListItem>
              <ListItem>
                <Text as="span" fontWeight="bold">Wait 10 days</Text> — After the timer expires, your crypto is automatically released back to your wallet. No action needed
              </ListItem>
              <ListItem>
                <Text as="span" fontWeight="bold">Need it earlier? (Emergency)</Text> — A validator on the 2-of-3 multisig is contacted. They reach out to you directly using system-provided contact details, verify you're not being coerced, then sign the approval with their own crypto wallet (MetaMask, hardware wallet, etc.)
              </ListItem>
              <ListItem>
                <Text as="span" fontWeight="bold">Validators earn a fee</Text> — Each verification pays a small fee directly to the validator's wallet via the smart contract. This incentivises thorough verification
              </ListItem>
              <ListItem>
                <Text as="span" fontWeight="bold">Server hack = useless</Text> — Approvals are on-chain wallet signatures, not database entries. Compromising our server doesn't help — the attacker would need to compromise each validator's individual wallet
              </ListItem>
            </OrderedList>
          </Box>

          <Divider />

          {/* Anti-Scam Features */}
          <Box>
            <Heading size="md" color="#2b6cb0" mb={3}>Built-In Anti-Scam Protection</Heading>
            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={3}>
              <HStack align="start" p={3} bg="gray.50" borderRadius="md">
                <Icon as={FiShield} color="orange.500" mt={1} />
                <Box>
                  <Text fontWeight="bold" fontSize="sm">"We will NEVER call you"</Text>
                  <Text fontSize="xs" color="gray.500">Displayed on every screen — if someone calls, it's a scam</Text>
                </Box>
              </HStack>
              <HStack align="start" p={3} bg="gray.50" borderRadius="md">
                <Icon as={FiLock} color="orange.500" mt={1} />
                <Box>
                  <Text fontWeight="bold" fontSize="sm">Destination whitelist</Text>
                  <Text fontSize="xs" color="gray.500">Pre-approved addresses only, 7-day delay to add new ones</Text>
                </Box>
              </HStack>
              <HStack align="start" p={3} bg="gray.50" borderRadius="md">
                <Icon as={FiUsers} color="orange.500" mt={1} />
                <Box>
                  <Text fontWeight="bold" fontSize="sm">Guardian independence</Text>
                  <Text fontSize="xs" color="gray.500">Guardians can't see who else has approved — stops manipulation</Text>
                </Box>
              </HStack>
              <HStack align="start" p={3} bg="gray.50" borderRadius="md">
                <Icon as={FiShield} color="orange.500" mt={1} />
                <Box>
                  <Text fontWeight="bold" fontSize="sm">Duress code</Text>
                  <Text fontSize="xs" color="gray.500">Secret PIN that looks like it works but actually freezes everything</Text>
                </Box>
              </HStack>
            </SimpleGrid>
          </Box>

          <Divider />

          {/* The Journey */}
          <Box>
            <Heading size="md" color="#2b6cb0" mb={3}>The Journey — Building This Together</Heading>
            <Text color="gray.600" mb={3}>We're building this product step by step, starting with real stories:</Text>
            <OrderedList spacing={3} pl={4} color="gray.600">
              <ListItem>
                <Text as="span" fontWeight="bold">Interview</Text> — Record Ed's hack story (and Tom's). Real experiences become the best product requirements AND the most powerful marketing
              </ListItem>
              <ListItem>
                <Text as="span" fontWeight="bold">Landing page</Text> — Build a public page with the story, the problem, and the solution. Start collecting interest
              </ListItem>
              <ListItem>
                <Text as="span" fontWeight="bold">Research & refine</Text> — Validate against existing solutions (Casa, Gnosis Safe, Argent). Find our unique angle: simplicity + time-first approach
              </ListItem>
              <ListItem>
                <Text as="span" fontWeight="bold">Build the module</Text> — Smart contracts (Ethereum + Bitcoin) + web dashboard following our existing Business Builder module architecture
              </ListItem>
              <ListItem>
                <Text as="span" fontWeight="bold">Test with $5</Text> — Deploy with $5 of BTC, $5 of ETH, and $5 of crypto gold. Run the full withdrawal flow end-to-end
              </ListItem>
              <ListItem>
                <Text as="span" fontWeight="bold">Phase 2: ZK proofs</Text> — Add zero-knowledge privacy so guardians can approve anonymously, making targeted social engineering of remaining guardians impossible
              </ListItem>
            </OrderedList>

            {/* ZK Proofs Diagram */}
            <Box mt={4} borderRadius="md" border="1px solid" borderColor="purple.200" bg="white" overflow="hidden">
              {zkDiagramLoading && (
                <Center h="200px">
                  <Spinner size="lg" color="purple.500" />
                </Center>
              )}
              {zkDiagramError && (
                <Box p={4} bg="red.50">
                  <Text color="red.700" fontSize="sm">Error loading diagram: {zkDiagramError}</Text>
                </Box>
              )}
              <Box
                ref={zkSvgContainerRef}
                p={4}
                sx={{
                  "& svg": {
                    width: "100%",
                    height: "auto",
                    maxWidth: "900px",
                    margin: "0 auto",
                    display: "block",
                  },
                }}
              />
            </Box>
          </Box>

          <Divider />

          {/* Next Step CTA */}
          <Box bg="blue.50" p={5} borderRadius="md" border="1px solid" borderColor="blue.200">
            <Heading size="md" color="#2b6cb0" mb={3}>Next Step: The Interview</Heading>
            <Text mb={3} color="gray.600">
              Let's start with step 1 — recording your story. This becomes the foundation for both the product design and the marketing. When are you free for a 30-minute chat?
            </Text>
          </Box>

          {/* How We Make Money */}
          <Box>
            <Heading size="md" color="#2b6cb0" mb={3}>
              <HStack>
                <Icon as={FiDollarSign} />
                <Text>How We Make Money</Text>
              </HStack>
            </Heading>
            <Text color="gray.600" mb={4}>
              Simple and transparent: a <strong>2% deposit fee</strong> when crypto enters the vault, plus a <strong>0.5% annual management fee</strong> (charged monthly at ~0.042%/month). Both fees are automatically split 50/50 between Tom and Ed via a smart contract into a shared treasury account. The more crypto we protect, the more we earn — our incentives are fully aligned with keeping your assets safe.
            </Text>

            {/* Embedded Excalidraw Diagram */}
            <Box borderRadius="md" border="1px solid" borderColor="gray.200" bg="white" overflow="hidden">
              {diagramLoading && (
                <Center h="200px">
                  <Spinner size="lg" color="blue.500" />
                </Center>
              )}
              {diagramError && (
                <Box p={4} bg="red.50">
                  <Text color="red.700" fontSize="sm">Error loading diagram: {diagramError}</Text>
                </Box>
              )}
              <Box
                ref={svgContainerRef}
                p={4}
                sx={{
                  "& svg": {
                    width: "100%",
                    height: "auto",
                    maxWidth: "800px",
                    margin: "0 auto",
                    display: "block",
                  },
                }}
              />
            </Box>

            <SimpleGrid columns={{ base: 1, md: 4 }} spacing={3} mt={4}>
              <Box p={3} bg="orange.50" borderRadius="md" textAlign="center">
                <Text fontWeight="bold" color="orange.700">2% Deposit</Text>
                <Text fontSize="xs" color="gray.500">One-time on entry</Text>
              </Box>
              <Box p={3} bg="purple.50" borderRadius="md" textAlign="center">
                <Text fontWeight="bold" color="purple.700">0.5% Annual</Text>
                <Text fontSize="xs" color="gray.500">Charged monthly</Text>
              </Box>
              <Box p={3} bg="green.50" borderRadius="md" textAlign="center">
                <Text fontWeight="bold" color="green.700">50/50 Split</Text>
                <Text fontSize="xs" color="gray.500">Tom & Ed, automatic</Text>
              </Box>
              <Box p={3} bg="blue.50" borderRadius="md" textAlign="center">
                <Text fontWeight="bold" color="blue.700">Shared Treasury</Text>
                <Text fontSize="xs" color="gray.500">Smart contract auto-split</Text>
              </Box>
            </SimpleGrid>

            {/* Competition Link */}
            <Box mt={4}>
              <Link as={RouterLink} to="/updates/safe-crypto-competition" color="blue.500" fontSize="sm" fontWeight="bold">
                See how we compare to Coinbase, BitGo, Fireblocks, Casa & Anchorage →
              </Link>
            </Box>
          </Box>

          <Divider />

          {/* Partnership Proposal */}
          <Box>
            <Heading size="md" color="#2b6cb0" mb={3}>Partnership Proposal — Tom & Ed</Heading>
            <Text color="gray.600" mb={4}>
              After what happened with your seed words being stolen — and Tom's Google password being leaked online — it's clear there's a real problem here. Centralised systems fail. Let's build something decentralised that actually works. Simple deal, between mates, no BS.
            </Text>

            {/* The Deal */}
            <SimpleGrid columns={{ base: 1, md: 4 }} spacing={4} mb={4}>
              <Box p={4} bg="orange.50" borderRadius="md" border="1px solid" borderColor="orange.200" textAlign="center">
                <Text fontSize="2xl" fontWeight="bold" color="orange.700">2%</Text>
                <Text fontSize="sm" color="gray.600">Deposit fee (one-time)</Text>
              </Box>
              <Box p={4} bg="purple.50" borderRadius="md" border="1px solid" borderColor="purple.200" textAlign="center">
                <Text fontSize="2xl" fontWeight="bold" color="purple.700">0.5%</Text>
                <Text fontSize="sm" color="gray.600">Annual fee (monthly)</Text>
              </Box>
              <Box p={4} bg="green.50" borderRadius="md" border="1px solid" borderColor="green.200" textAlign="center">
                <Text fontSize="2xl" fontWeight="bold" color="green.700">50/50</Text>
                <Text fontSize="sm" color="gray.600">Split automatically</Text>
              </Box>
              <Box p={4} bg="blue.50" borderRadius="md" border="1px solid" borderColor="blue.200" textAlign="center">
                <Text fontSize="2xl" fontWeight="bold" color="blue.700">$0</Text>
                <Text fontSize="sm" color="gray.600">No upfront cost to us</Text>
              </Box>
            </SimpleGrid>

            {/* Revenue Table */}
            <Box overflowX="auto" mb={4}>
              <Table variant="simple" size="sm">
                <Thead>
                  <Tr>
                    <Th>Crypto Deposited</Th>
                    <Th isNumeric>2% Deposit Fee</Th>
                    <Th isNumeric>0.5% Annual Fee</Th>
                    <Th isNumeric>Year 1 Total</Th>
                    <Th isNumeric>Each (50/50)</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  <Tr>
                    <Td>$100,000</Td>
                    <Td isNumeric>$2,000</Td>
                    <Td isNumeric>$500/yr</Td>
                    <Td isNumeric>$2,500</Td>
                    <Td isNumeric color="green.600">$1,250 each</Td>
                  </Tr>
                  <Tr>
                    <Td>$1,000,000</Td>
                    <Td isNumeric>$20,000</Td>
                    <Td isNumeric>$5,000/yr</Td>
                    <Td isNumeric>$25,000</Td>
                    <Td isNumeric color="green.600">$12,500 each</Td>
                  </Tr>
                  <Tr bg="green.50">
                    <Td fontWeight="bold">$10,000,000</Td>
                    <Td isNumeric fontWeight="bold">$200,000</Td>
                    <Td isNumeric fontWeight="bold">$50,000/yr</Td>
                    <Td isNumeric fontWeight="bold">$250,000</Td>
                    <Td isNumeric fontWeight="bold" color="green.600">$125,000 each</Td>
                  </Tr>
                </Tbody>
              </Table>
            </Box>

            {/* What Each Person Brings */}
            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4} mb={4}>
              <Box p={4} borderRadius="md" border="1px solid" borderColor="blue.200" bg="blue.50">
                <Text fontWeight="bold" color="blue.700" mb={2}>Tom</Text>
                <UnorderedList spacing={1} color="gray.600" fontSize="sm">
                  <ListItem>Platform development (Business Builder)</ListItem>
                  <ListItem>Smart contract development</ListItem>
                  <ListItem>Hosting, deployment & infrastructure</ListItem>
                  <ListItem>Marketing through existing channels</ListItem>
                </UnorderedList>
              </Box>
              <Box p={4} borderRadius="md" border="1px solid" borderColor="orange.200" bg="orange.50">
                <Text fontWeight="bold" color="orange.700" mb={2}>Ed</Text>
                <UnorderedList spacing={1} color="gray.600" fontSize="sm">
                  <ListItem>Crypto market expertise & connections</ListItem>
                  <ListItem>First user and product tester</ListItem>
                  <ListItem>Story & experience for marketing</ListItem>
                  <ListItem>Co-marketing and network referrals</ListItem>
                </UnorderedList>
              </Box>
            </SimpleGrid>

            {/* Next Steps */}
            <Box bg="blue.50" p={4} borderRadius="md" border="1px solid" borderColor="blue.200">
              <Text fontWeight="bold" color="blue.700" mb={2}>Next Steps</Text>
              <OrderedList spacing={1} color="gray.600" fontSize="sm">
                <ListItem>Schedule the interview (30 mins, whenever suits)</ListItem>
                <ListItem>Set up the shared treasury account</ListItem>
                <ListItem>First test deposit — $5 each of BTC, ETH, and gold</ListItem>
              </OrderedList>
            </Box>
          </Box>

          <Divider />

          {/* Sign off */}
          <Box>
            <Text color="gray.600">Simple deal, real problem, built from real experience. Let's do it.</Text>
            <Text mt={4} color="gray.600">
              <strong>Tom</strong> & <strong>Ed</strong>
            </Text>
          </Box>
        </VStack>
      </Container>
      <FooterWithFourColumns />
    </>
  );
};

export default SafeCryptoCustody;
